import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { ConnectError } from '@connectrpc/connect'
import {
  BgPowerTypeMixDetailSchema,
  LastUnitOfBgPlantDetailSchema,
  PlanCategoryMismatchDetailSchema,
  type PowerType,
  PowerTypeChangeConfirmationDetailSchema,
  PowerTypeMixDetailSchema,
  UndecidedPlantDetailSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  civilDateToInputValue,
  compareCivilDate,
  protoDateToCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { planCategoryLabel } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/planCategory'
import {
  derivedPowerTypeLabel,
  leadingSpaceIfLatin,
} from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'

/**
 * 電源種別の整合の歯止め (電源ユニットの保存・発電販売計画マスタの保存が返す型つきの error detail) を、
 * 画面の文言・確認の中身にする。系統コード・BG・日付は detail の項目から埋め (文字列を解釈しない)、
 * 電源種別・計画区分は他画面と同じラベルで出す。
 */

const KEY = 'page:adminOcctoMaster.powerTypeIntegrity'

type ProtoDate = Parameters<typeof protoDateToCivilDate>[0]

/** 日付は ISO 形式 (2027-04-01)。画面の文言 (spec の文) と同じ。 */
function dateText(date: ProtoDate): string {
  return civilDateToInputValue(protoDateToCivilDate(date))
}

/** 前日 (ISO 形式)。「…までにしてください」の案内に使う。 */
function previousDateText(date: ProtoDate): string {
  const civil = protoDateToCivilDate(date)
  if (civil === undefined) return ''
  const previous = new Date(
    Date.UTC(civil.year, civil.month - 1, civil.day - 1),
  )
  return previous.toISOString().slice(0, 10)
}

/** 期間の途中から始まる食い違いの「(2027-04-01 から)」。今日から食い違うなら付けない。 */
function fromText(t: TFunction, date: ProtoDate): string {
  const civil = protoDateToCivilDate(date)
  if (civil === undefined || compareCivilDate(civil, todayCivilDate()) <= 0) {
    return ''
  }
  return t(`${KEY}.bgMixFrom`, { date: dateText(date) })
}

/**
 * 保存を止めた整合の理由 (E1 期間の重なり・E4 最後のユニット・E2 BG の混在・E3 未定・計画区分の不一致) の文言。
 * どれでもなければ undefined。
 */
/** E1 の文言に並べる 1 ユニット (名前と電源種別)。 */
export type PowerTypeMixUnitText = { unitName: string; powerType: PowerType }

/**
 * E1 (同じ系統コードの同じ期間に電源種別の違うユニットがある) の文言 (S-2.1)。何があるかを先に言う。
 * 入力中のエラー (電源ユニットの登録ダイアログ・詳細の編集) と、保存時に backend が止めた時の文言で同じ。
 * ユニットは「1号機: 非調整電源」の形で、複数は「、」区切り。
 */
export function unitPeriodMixMessage(
  t: TFunction,
  plantCode: string,
  units: readonly PowerTypeMixUnitText[],
): string {
  return t(`${KEY}.unitPeriodMix`, {
    plantCode,
    units: units
      .map((unit) =>
        t(`${KEY}.unitPeriodMixUnit`, {
          unit: unit.unitName,
          powerType: derivedPowerTypeLabel(unit.powerType),
        }),
      )
      .join(t(`${KEY}.unitJoiner`)),
  })
}

export function powerTypeIntegrityMessage(
  t: TFunction,
  err: unknown,
  caller: 'bg' | undefined = undefined,
): string | undefined {
  if (!(err instanceof ConnectError)) return undefined

  const mix = err.findDetails(PowerTypeMixDetailSchema)[0]
  if (mix !== undefined) {
    return unitPeriodMixMessage(
      t,
      mix.plantCode,
      mix.units.map((unit) => ({
        unitName: unit.unitName,
        powerType: unit.powerType,
      })),
    )
  }

  const last = err.findDetails(LastUnitOfBgPlantDetailSchema)[0]
  if (last !== undefined) {
    return t(`${KEY}.lastUnit`, {
      plantCode: last.plantCode,
      bgCode: last.bgCode,
      date: dateText(last.firstUncoveredDate),
      previousDate: previousDateText(last.firstUncoveredDate),
    })
  }

  const bgMix = err.findDetails(BgPowerTypeMixDetailSchema)[0]
  if (bgMix !== undefined) {
    return t(`${KEY}.bgMix`, {
      bgCode: bgMix.bgCode,
      from: fromText(t, bgMix.firstMixedDate),
      groups: bgMix.groups
        .map((group) =>
          t(`${KEY}.bgMixGroup`, {
            powerType: derivedPowerTypeLabel(group.powerType),
            plantCodes: group.plantCodes.join(t(`${KEY}.listJoiner`)),
          }),
        )
        .join(t(`${KEY}.groupJoiner`)),
    })
  }

  const undecided = err.findDetails(UndecidedPlantDetailSchema)[0]
  if (undecided !== undefined) {
    return undecided.plants
      .map((plant) =>
        t(plant.hasAnyUnit ? `${KEY}.undecidedGap` : `${KEY}.undecidedNoUnit`, {
          plantCode: plant.plantCode,
          date: dateText(plant.firstUndecidedDate),
        }),
      )
      .join('\n')
  }

  const category = err.findDetails(PlanCategoryMismatchDetailSchema)[0]
  if (category !== undefined) {
    if (caller === 'bg' && category.submitterCode !== '') {
      return t(`${KEY}.planCategoryForBg`, {
        plantCode: category.plantCode,
        powerType: derivedPowerTypeLabel(category.powerType),
        planCategory: planCategoryLabel(category.planCategory),
        date: dateText(category.firstDate),
        submitterCode: category.submitterCode,
      })
    }
    return t(
      category.submitterCode === ''
        ? `${KEY}.planCategory`
        : `${KEY}.planCategoryOfMaster`,
      {
        plantCode: category.plantCode,
        powerType: derivedPowerTypeLabel(category.powerType),
        planCategory: planCategoryLabel(category.planCategory),
        // 「…の{planCategory}の…」の途中に補間する (英字で始まる計画区分名「FIT 特例電源」だけ前に半角スペース)
        planCategoryInSentence: leadingSpaceIfLatin(
          planCategoryLabel(category.planCategory),
        ),
        // 「電源種別を{powerType}にできません」の途中に補間する。英字で始まる電源種別 (「FIP 電源」「FIT 特例①」) だけ前に半角スペース
        // (conventions.md「半角スペースの入れどころ」。日本語の名前の前後には入れない。DE の指示 2026-11-04)
        powerTypeInSentence: leadingSpaceIfLatin(
          derivedPowerTypeLabel(category.powerType),
        ),
        submitterCode: category.submitterCode,
        date: dateText(category.firstDate),
      },
    )
  }

  return undefined
}

/** 確認ダイアログに出す内容と、送り直しに付ける確認済みのユニット。 */
export type PowerTypeChangeConfirmation = {
  title: string
  description: string
  units: string[]
  confirmedUnits: Array<{ id: string; updatedAt: Timestamp }>
}

/**
 * 電源種別だけを変える保存が「同じ系統コードの他のユニットも変わる」で止まった時の確認の中身。
 * 確認が要るエラーでなければ undefined。確認済みのユニットは、見せた (id, 更新時刻) をそのまま送り返す
 * (見せた後に他のユニットが更新されていたら、サーバーが改めて確認を求める)。
 */
export function powerTypeChangeConfirmationOf(
  t: TFunction,
  err: unknown,
): PowerTypeChangeConfirmation | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const detail = err.findDetails(PowerTypeChangeConfirmationDetailSchema)[0]
  if (detail === undefined) return undefined
  const to = derivedPowerTypeLabel(detail.to)
  // 変わるのは種別が違うユニットだけ。見せるのは backend が返すユニット (同じ系統コードで有効期間の重なりが
  // 推移的につながるユニット。DE 判断 2026-10-31 Q2=A) で、確認の照合のためそのまま送り返す
  const changing = detail.units.filter((unit) => unit.powerType !== detail.to)
  return {
    title: t(`${KEY}.change.title`),
    description: t(`${KEY}.change.body`, {
      plantCode: detail.plantCode,
      count: changing.length,
      names: changing.map((unit) => unit.unitName).join(t(`${KEY}.unitJoiner`)),
      to,
      // 「も」の直後に補間する (英字で始まる種別名だけ前に半角スペース)
      toInSentence: leadingSpaceIfLatin(to),
    }),
    // 変わるユニットは「変える前 → 変えた後」、もう同じ種別のユニットは今の種別だけ
    units: detail.units.map((unit) =>
      t(
        unit.powerType === detail.to
          ? `${KEY}.change.unitUnchanged`
          : `${KEY}.change.unit`,
        {
          unit: unit.unitName,
          powerType: derivedPowerTypeLabel(unit.powerType),
          to,
        },
      ),
    ),
    confirmedUnits: detail.units.flatMap((unit) =>
      unit.updatedAt === undefined
        ? []
        : [{ id: unit.id, updatedAt: unit.updatedAt }],
    ),
  }
}
